import React, { useState, useEffect } from 'react';
import { getDevices, getPositions, getDrivers, getEventsReport, getPositionsByIds } from '../traccar';
import { getAlerts, getDriverAlerts, acceptVin } from '../vehicleAlerts';
import { playOpen, playClose } from '../clickSound';
import { METRICS, OPS, getRules, saveRules } from '../customAlerts';
import { BUILTIN_ALERTS, getAlertPrefs, saveAlertPrefs } from '../alertPrefs';
import { getDayAlerts } from '../alertLog';
import { IS_TAGALONG } from '../App';
import { fetchAlertHistory, sendTestPush } from '../push';
import { ackHealth, isAcked, mergeServerAcks, rearmCleared } from '../healthAck';
import AlertDetailModal from '../AlertDetailModal';

const dayKey = (d) => d.toLocaleDateString('en-CA');

// Traccar records driving alarms (harsh braking, hard acceleration, speeding,
// etc.) as events. These never went through push, so the history list — which
// only merged push + the app's own log — was silently dropping them. This maps
// an alarm string to a friendly row so EVERY alert type shows in the history.
const alarmMeta = (raw) => {
  const a = String(raw || '').toLowerCase();
  if (!a) return null;
  if (/overspeed|speed/.test(a)) return { title: 'Speeding', icon: '🚧', sev: 'warn' };
  if (/brak/.test(a)) return { title: 'Hard braking', icon: '🛑', sev: 'warn' };
  if (/accel/.test(a)) return { title: 'Hard acceleration', icon: '💨', sev: 'warn' };
  if (/corner/.test(a)) return { title: 'Hard cornering', icon: '↩️', sev: 'warn' };
  if (/tow|movement/.test(a)) return { title: 'Unexpected movement', icon: '🚨', sev: 'bad' };
  if (/crash|accident|impact/.test(a)) return { title: 'Possible crash', icon: '🚨', sev: 'bad' };
  if (/jamming/.test(a)) return { title: 'Signal jamming', icon: '📡', sev: 'bad' };
  if (/lowbattery|low_battery/.test(a)) return { title: 'Low battery', icon: '🔋', sev: 'warn' };
  if (/fuel/.test(a)) return { title: 'Fuel event', icon: '⛽', sev: 'warn' };
  if (/sos/.test(a)) return { title: 'SOS', icon: '🆘', sev: 'bad' };
  // any other alarm we don't have a nice name for: still show it, generically
  return { title: a.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^\w/, (c) => c.toUpperCase()), icon: '⚠️', sev: 'warn' };
};

// native app = dark glass skin
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
const TXT = IS_NATIVE ? '#e8ebf5' : '#0f172a';
const TXT_MUTED = IS_NATIVE ? '#9aa0b4' : '#64748b';
// dark glass card for native, original white/gradient card on web
const ROW_CARD = IS_NATIVE
  ? { background: 'linear-gradient(160deg, rgba(255,255,255,0.085), rgba(255,255,255,0.035))', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '20px', overflow: 'hidden', color: TXT }
  : {
    background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, rgba(15,23,42,0.4), rgba(249,115,22,0.55), rgba(255,255,255,0.9))',
    backgroundOrigin: 'padding-box, border-box', backgroundClip: 'padding-box, border-box',
  };

const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: '10px', fontWeight: 700, color: TXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };
const emptyRule = () => ({ id: `R${Date.now()}`, name: '', metric: 'speed', op: 'gt', value: '', severity: 'warn', deviceId: 'all', enabled: true });

const SEV = {
  bad: { bg: '#fef2f2', border: '#ef4444', text: '#b91c1c' },
  warn: { bg: '#fffbeb', border: '#f59e0b', text: '#b45309' },
};

export default function AlertsPage() {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(null);
  const [, forceRefresh] = useState(0);
  const [rules, setRules] = useState(getRules());
  const [ruleEdit, setRuleEdit] = useState(null); // rule being created/edited
  const [historyDay, setHistoryDay] = useState(new Date());
  const [historyOpen, setHistoryOpen] = useState(true);
  const [detail, setDetail] = useState(null); // logged alert whose location is open
  // Alerts the SERVER pushed (including while the app was closed), so the history
  // isn't limited to times the app happened to be open.
  const [serverAlerts, setServerAlerts] = useState([]);
  const [sync, setSync] = useState({ state: 'loading' });
  // Traccar driving-alarm events for the selected day (harsh brake/accel/speeding
  // etc.), enriched with the position where each fired so the detail map works.
  const [dayEvents, setDayEvents] = useState([]);
  // Alert History vehicle filter — 'all' or a specific car's display name.
  const [alertCar, setAlertCar] = useState('all');
  useEffect(() => {
    let stop = false;
    const pull = () => fetchAlertHistory()
      .then((a) => { if (!stop) { setServerAlerts(a); setSync({ state: 'ok', n: a.length }); } })
      .catch((e) => { if (!stop) setSync({ state: 'err', msg: e.message || 'offline' }); });
    pull();
    const t = setInterval(pull, 20000);
    // also refresh the moment the app comes back to the foreground — that's
    // exactly when a push just arrived and the user is coming to look at it
    const onWake = () => pull();
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      stop = true; clearInterval(t);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, []);
  // convert a server record into the same shape the history list renders
  const fromServer = (a) => ({
    day: new Date(a.t).toLocaleDateString('en-CA'),
    t: a.t,
    entity: a.car || '',
    severity: a.sev === 'bad' ? 'bad' : 'warn',
    title: a.title || '',
    icon: '🔔',
    info: a.body || '',
    vitals: a.vitals || null,
    place: (a.lat != null && a.lng != null)
      ? { lat: Number(a.lat), lng: Number(a.lng), speed: a.spd, at: new Date(a.t).getTime(), imei: a.imei || '', device: a.car || '' }
      : null,
    _srv: 1,
  });
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 10000); return () => clearInterval(t); }, []);

  // pull Traccar driving-alarm events for the selected history day, across every
  // device, and attach each one's location — so hard brakes, hard acceleration
  // and speeding show up in the history alongside pushed alerts.
  useEffect(() => {
    let cancelled = false;
    if (!devices.length) { setDayEvents([]); return undefined; }
    const start = new Date(historyDay); start.setHours(0, 0, 0, 0);
    const end = new Date(historyDay); end.setHours(23, 59, 59, 999);
    const from = start.toISOString(), to = end.toISOString();
    (async () => {
      try {
        const per = await Promise.all(devices.map((d) => getEventsReport(d.id, from, to).catch(() => []).then((ev) => ({ d, ev }))));
        const rows = [];
        per.forEach(({ d, ev }) => {
          const nm = (d.attributes || {}).displayName || d.name;
          ev.forEach((e) => {
            if (e.type !== 'alarm' && !(e.attributes && e.attributes.alarm)) return;
            const meta = alarmMeta((e.attributes || {}).alarm || e.type);
            if (!meta) return;
            const when = e.eventTime || e.serverTime;
            rows.push({ day: dayKey(new Date(when)), t: when, entity: nm, severity: meta.sev, title: meta.title, icon: meta.icon, info: '', positionId: e.positionId, _ev: 1 });
          });
        });
        // attach the position where each alarm fired (for the detail map)
        const ids = [...new Set(rows.map((r) => r.positionId).filter(Boolean))];
        let posById = {};
        try { const pl = await getPositionsByIds(ids); posById = Object.fromEntries((pl || []).map((p) => [p.id, p])); } catch { /* ignore */ }
        rows.forEach((r) => {
          const p = posById[r.positionId];
          if (p && p.latitude != null) r.place = { lat: p.latitude, lng: p.longitude, speed: Math.round((p.speed || 0) * 1.15078), at: new Date(r.t).getTime(), device: r.entity };
        });
        if (!cancelled) setDayEvents(rows);
      } catch { if (!cancelled) setDayEvents([]); }
    })();
    return () => { cancelled = true; };
  }, [historyDay, devices]);

  // Some alerts genuinely have no position of their own — "tracker offline" is
  // the obvious one, since the device had already stopped reporting. Rather than
  // show an empty screen, fall back to that car's LAST KNOWN position so the
  // owner still sees where it was, clearly labelled as last known.
  const enrich = (e) => {
    if (e.place && e.place.lat != null) return e;
    const dev = devices.find((d) => ((d.attributes || {}).displayName || d.name) === e.entity || d.name === e.entity);
    const pos = dev && positions.find((p) => p.deviceId === dev.id);
    if (!dev || !pos || pos.latitude == null) return e;
    return {
      ...e,
      lastKnown: true,
      place: {
        lat: pos.latitude, lng: pos.longitude,
        speed: Math.round((pos.speed || 0) * 1.15078),
        at: e.t ? new Date(e.t).getTime() : Date.now(),
        imei: dev.uniqueId || '', device: e.entity,
      },
    };
  };

  // show the customer's own name for the car (displayName), not the raw unit id
  const dName = (d) => (d ? ((d.attributes || {}).displayName || d.name) : '');
  const persistRules = (next) => { setRules(next); saveRules(next); };
  // per-car built-in alert on/off toggles (stored in alertPrefs)
  const [prefsV, setPrefsV] = useState(0);
  const toggleBuiltin = (devId, key) => {
    const p = getAlertPrefs(devId);
    const cur = p.builtins[key] || { on: true, channel: 'app' };
    p.builtins[key] = { ...cur, on: !cur.on };
    saveAlertPrefs(devId, p);
    setPrefsV((v) => v + 1);
  };
  const saveRule = () => {
    if (!ruleEdit.name || ruleEdit.value === '') { alert('Give the rule a name and a threshold value.'); return; }
    const exists = rules.some((r) => r.id === ruleEdit.id);
    persistRules(exists ? rules.map((r) => (r.id === ruleEdit.id ? ruleEdit : r)) : [...rules, ruleEdit]);
    setRuleEdit(null);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [devs, poss, drs] = await Promise.all([getDevices(), getPositions(), getDrivers()]);
        if (cancelled) return;
        mergeServerAcks(devs); // sync resolves made on other profiles/tabs
        setDevices(devs);
        setPositions(poss);
        setDrivers(IS_TAGALONG ? [] : drs); // customers don't have "drivers"
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const rows = devices.map((d) => {
    const position = positions.find((p) => p.deviceId === d.id);
    // if a fault has cleared, drop its "resolved" mark so a recurrence re-alerts
    rearmCleared(d.id, (position && position.attributes) || {});
    // hide alerts the user has already marked resolved (until the reading changes)
    const alerts = getAlerts(d, position).filter((al) => !(al.ackKey && isAcked(d.id, al.ackKey, al.ackSig)));
    return { device: d, position, alerts };
  });

  const driverRows = drivers.map((d) => ({ driver: d, alerts: getDriverAlerts(d) }));
  const totalAlerts = rows.reduce((s, r) => s + r.alerts.length, 0) + driverRows.reduce((s, r) => s + r.alerts.length, 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🔔 Alerts</h1>
        <p>
          {error ? `🔴 ${error}`
            : totalAlerts === 0 ? 'All vehicles are clear — no active alerts.'
            : `${totalAlerts} active alert(s) across your fleet. Click a vehicle to see the details.`}
        </p>
        {/* Diagnostic: fire a push to THIS phone to check the whole chain end-to-end. */}
        <button
          onClick={async () => {
            const r = await sendTestPush();
            const carsLine = r.scopedCars
              ? `\n\nCars the server sends YOU alerts for: ${r.scopedCars.length ? r.scopedCars.join(', ') : '(none — no car is assigned to this account)'}.`
                + (r.blockedCars && r.blockedCars.length ? `\n\n⛔ Assigned but SILENCED (membership expired/stopped — renew on the Devices tab): ${r.blockedCars.join(', ')}.` : '')
              : '';
            if (r.ok) alert(`✅ Test alert sent to ${r.sent} device(s). If it doesn't arrive on your locked phone in a few seconds, it's an APNs environment mismatch (sandbox vs production) or a dead token.${carsLine}\n\nIf a car you expect alerts from is NOT in that list, it's assigned to a different account — assign it to THIS account on the Devices tab.`);
            else {
              const why = {
                'no-tokens': 'This account has no registered phone. Open the app on the phone and ALLOW notifications — and make sure you signed in as the account the car is assigned to.',
                'apns-not-configured': 'The server has no Apple push key set (APNS_KEY / KEY_ID / TEAM_ID). Set those on the backend.',
                'no-backend': 'No backend is configured for this build.',
                network: 'Could not reach the server.',
              }[r.reason] || r.reason || 'Unknown error.';
              alert(`❌ Test alert did not send.\nReason: ${r.reason}\n\n${why}`);
            }
          }}
          style={{ marginTop: '8px', background: 'linear-gradient(135deg,#f97316,#f4511e)', color: 'white', border: 'none', borderRadius: '9px', padding: '9px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
        >
          🔔 Send a test alert to this phone
        </button>
      </div>

      {/* rule editor */}
      {ruleEdit && (
        <div onClick={() => setRuleEdit(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(460px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h3 style={{ margin: '0 0 14px', fontFamily: 'Poppins, sans-serif' }}>{rules.some((r) => r.id === ruleEdit.id) ? 'Edit Alert Rule' : 'New Alert Rule'}</h3>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>Alert name</span><input style={inp} placeholder="e.g. Engine overheating" value={ruleEdit.name} onChange={(e) => setRuleEdit({ ...ruleEdit, name: e.target.value })} /></div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>When this reading…</span>
              <select style={inp} value={ruleEdit.metric} onChange={(e) => setRuleEdit({ ...ruleEdit, metric: e.target.value })}>
                {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}{m.unit ? ` (${m.unit})` : ''}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Condition</span>
                <select style={inp} value={ruleEdit.op} onChange={(e) => setRuleEdit({ ...ruleEdit, op: e.target.value })}>
                  {OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div style={{ width: '110px' }}><span style={lbl}>Value</span><input type="number" style={inp} value={ruleEdit.value} onChange={(e) => setRuleEdit({ ...ruleEdit, value: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Severity</span>
                <select style={inp} value={ruleEdit.severity} onChange={(e) => setRuleEdit({ ...ruleEdit, severity: e.target.value })}>
                  <option value="warn">⚠️ Warning (amber)</option>
                  <option value="bad">🚨 Critical (red)</option>
                </select>
              </div>
              <div style={{ flex: 1 }}><span style={lbl}>Apply to</span>
                <select style={inp} value={ruleEdit.deviceId} onChange={(e) => setRuleEdit({ ...ruleEdit, deviceId: e.target.value })}>
                  <option value="all">All vehicles</option>
                  {devices.map((d) => <option key={d.id} value={d.id}>{dName(d)}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600 }} onClick={() => setRuleEdit(null)}>Cancel</button>
              <button style={{ background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: 700 }} onClick={saveRule}>Save rule</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- alert history by calendar day ---- */}
      {(() => {
        const days = Array.from({ length: 14 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - i); return d; });
        // on the customer site, only show alerts for THEIR devices
        // Match names loosely (case + spacing), because the server labels an
        // alert with the car's display name while the app may have logged the
        // account name — an exact-match filter silently dropped server alerts.
        const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const allowed = new Set(devices.flatMap((d) => [d.name, (d.attributes || {}).displayName]).filter(Boolean).map(norm));
        const scoped = (a) => !IS_TAGALONG || allowed.has(norm(a.entity));
        // merge the app's own log with the server's push history, de-duping
        // anything recorded by both (same car + title within ~2 minutes)
        const srv = serverAlerts.map(fromServer);
        const mergeFor = (k) => {
          const local = getDayAlerts(k).filter(scoped);
          const remote = srv.filter((a) => a.day === k && (!IS_TAGALONG || !a.entity || allowed.has(norm(a.entity))));
          // The SERVER push history is the authoritative record of what was
          // actually alerted — show ALL of it. Three speeding pings (85/87/93 mph)
          // are three real events, not one, so we must NOT collapse push entries
          // against each other. Only the phone's own log and the Traccar events
          // get de-duped AGAINST the push history, so a single event the phone saw
          // AND the server pushed isn't listed twice.
          const out = [...remote];
          const addUnique = (r) => {
            const dup = out.some((l) => norm(l.entity) === norm(r.entity)
              && String(l.title).slice(0, 18) === String(r.title).slice(0, 18)
              && Math.abs(new Date(l.t) - new Date(r.t)) < 90000);
            if (!dup) out.push(r);
          };
          local.forEach(addUnique);
          // Traccar driving-alarm events (only present for the selected day)
          dayEvents.filter((a) => a.day === k && (!IS_TAGALONG || !a.entity || allowed.has(norm(a.entity)))).forEach(addUnique);
          return out.sort((a, b) => (b.t || '').localeCompare(a.t || ''));
        };
        // per-car filter for the history (dropdown below the day chips)
        const carOptions = [...new Set(devices.map((d) => (d.attributes || {}).displayName || d.name).filter(Boolean))].sort();
        const carMatch = (a) => alertCar === 'all' || norm(a.entity) === norm(alertCar);
        const dayCount = (k) => mergeFor(k).filter(carMatch).length;
        const list = mergeFor(dayKey(historyDay)).filter(carMatch);
        const isToday = dayKey(historyDay) === dayKey(new Date());
        const sevStyle = { bad: { bg: '#fef2f2', color: '#b91c1c' }, warn: { bg: '#fffbeb', color: '#b45309' } };
        return (
          <div className="card" style={{ maxWidth: '760px', marginBottom: '20px', padding: '16px' }}>
            <div onClick={() => { (historyOpen ? playClose : playOpen)(); setHistoryOpen(!historyOpen); }} style={{ display: 'flex', alignItems: 'center', marginBottom: historyOpen ? '10px' : 0, cursor: 'pointer' }}>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: TXT, flex: 1 }}>📅 Alert History</div>
              <span style={{ fontSize: '12px', color: TXT_MUTED, marginRight: '10px' }}>{list.length} on {isToday ? 'today' : historyDay.toLocaleDateString()}</span>
              {sync.state === 'err' && (
                <span title={`Server history unavailable: ${sync.msg}`} style={{ fontSize: '10px', fontWeight: 700, color: '#f59e0b', marginRight: '8px' }}>
                  ⚠︎ {sync.msg}
                </span>
              )}
              <span style={{ color: TXT_MUTED, fontSize: '15px', transform: historyOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
            </div>
            {historyOpen && (<>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', overflowX: 'auto', overflowY: 'hidden', paddingTop: '8px', paddingBottom: '8px' }}>
              {days.map((d) => {
                const sel = dayKey(d) === dayKey(historyDay);
                const n = dayCount(dayKey(d));
                return (
                  <button key={dayKey(d)} className={`seg-frost ${sel ? 'on' : ''}`} onClick={() => setHistoryDay(d)} style={{
                    flexShrink: 0, width: '54px', height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '6px 0', borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
                    background: sel ? 'linear-gradient(135deg, #f97316, #f4511e)' : (IS_NATIVE ? 'rgba(255,255,255,0.07)' : 'white'), color: sel ? 'white' : (IS_NATIVE ? TXT : '#334155'),
                    border: '1px solid ' + (sel ? 'transparent' : (IS_NATIVE ? 'rgba(255,255,255,0.12)' : '#e2e8f0')), position: 'relative',
                  }}>
                    <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.8 }}>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px' }}>{d.getDate()}</div>
                    {n > 0 && <span style={{ position: 'absolute', top: '-4px', right: '-4px', minWidth: '16px', height: '16px', borderRadius: '999px', background: sel ? 'white' : '#ef4444', color: sel ? '#f4511e' : 'white', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{n}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: TXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Vehicle</span>
              <select
                value={alertCar}
                onChange={(e) => { playOpen(); setAlertCar(e.target.value); }}
                style={{ flex: 1, padding: '9px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: TXT, background: IS_NATIVE ? 'rgba(255,255,255,0.07)' : 'white', border: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.14)' : '#e2e8f0'}` }}
              >
                <option value="all">All cars</option>
                {carOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {alertCar !== 'all' && (
                <button onClick={() => { playClose(); setAlertCar('all'); }} style={{ padding: '8px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.14)' : '#e2e8f0'}`, background: 'transparent', color: TXT_MUTED, cursor: 'pointer' }}>Clear</button>
              )}
            </div>
            <div style={{ marginTop: '8px', maxHeight: '62vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', borderTop: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
              {list.length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>{alertCar === 'all' ? 'No alerts recorded on this day.' : `No alerts for ${alertCar} on this day.`}</p>}
              <div style={{ fontSize: '10px', color: TXT_MUTED, padding: '2px 4px 8px' }}>
                {sync.state === 'ok'
                  ? `${list.filter((x) => x._srv).length} from push · ${list.filter((x) => !x._srv).length} from this phone · ${sync.n} stored on server`
                  : sync.state === 'err' ? `Push history unavailable — ${sync.msg}` : 'Syncing push history…'}
              </div>
              {list.map((e, i) => {
                const s = sevStyle[e.severity] || sevStyle.warn;
                const hasLoc = e.place && e.place.lat != null;
                return (
                  <div key={i} onClick={() => setDetail(enrich(e))} title="Open alert detail" style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '46px', padding: '10px 6px', borderBottom: `1px solid ${IS_NATIVE ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}`, fontSize: '13px', cursor: 'pointer', WebkitTapHighlightColor: 'rgba(255,255,255,0.12)' }}>
                    <span style={{ fontSize: '11px', color: TXT_MUTED, width: '68px', flexShrink: 0 }}>{new Date(e.t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <span style={{ fontWeight: 600, color: TXT, flexShrink: 0 }}>{e.entity}</span>
                    <span style={{ color: TXT_MUTED, flex: 1 }}>{e.icon} {e.title}{hasLoc && <span style={{ color: '#60a5fa', fontWeight: 700 }}> · 📍</span>}</span>
                    <span style={{ color: TXT_MUTED, fontSize: '15px', flexShrink: 0 }}>›</span>
                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', background: s.bg, color: s.color }}>{e.severity === 'bad' ? 'CRITICAL' : 'WARNING'}</span>
                  </div>
                );
              })}
            </div>
            </>)}
          </div>
        );
      })()}

      {/* ---- custom alert rules (below the history so the thrown alerts show first) ---- */}
      <div className="card" style={{ maxWidth: '760px', marginBottom: '20px', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: TXT, flex: 1 }}>⚙️ Your Custom Alerts</div>
          <button style={{ padding: '7px 14px', borderRadius: '6px', fontWeight: 700, fontSize: '12px', background: 'linear-gradient(135deg, #f97316, #f4511e)', color: 'white' }} onClick={() => { playOpen(); setRuleEdit(emptyRule()); }}>+ New Alert Rule</button>
        </div>
        {rules.length === 0 && <p className="text-muted" style={{ fontSize: '13px', margin: 0 }}>No custom rules yet. Build your own — e.g. “alert if coolant is above 105 °C” or “fuel below 15%”.</p>}
        {rules.map((r) => {
          const m = METRICS.find((x) => x.key === r.metric); const o = OPS.find((x) => x.key === r.op);
          const dev = r.deviceId === 'all' ? 'All vehicles' : dName(devices.find((d) => String(d.id) === String(r.deviceId))) || 'Vehicle';
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 4px', borderTop: '1px solid #f1f5f9', fontSize: '13px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: r.enabled === false ? '#cbd5e1' : (r.severity === 'bad' ? '#ef4444' : '#f59e0b'), flexShrink: 0 }}></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: TXT }}>{r.name}</div>
                <div style={{ fontSize: '12px', color: TXT_MUTED }}>{dev} · {m && m.label} {o && o.label} {r.value}{m && m.unit}</div>
              </div>
              <button onClick={() => persistRules(rules.map((x) => (x.id === r.id ? { ...x, enabled: x.enabled === false } : x)))} style={{ fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', background: r.enabled === false ? '#f1f5f9' : '#f0fdf4', color: r.enabled === false ? '#64748b' : '#15803d' }}>{r.enabled === false ? 'OFF' : 'ON'}</button>
              <button onClick={() => { playOpen(); setRuleEdit({ ...r }); }} style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 700 }}>Edit</button>
              <button onClick={() => { if (window.confirm(`Delete rule “${r.name}”?`)) persistRules(rules.filter((x) => x.id !== r.id)); }} style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 700 }}>✕</button>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '760px' }}>
        {rows.map(({ device, position, alerts }) => {
          const open = openId === device.id;
          const worst = alerts.some((al) => al.severity === 'bad') ? 'bad' : alerts.length ? 'warn' : null;
          return (
            <div key={device.id} style={{ ...ROW_CARD, boxShadow: open ? '0 6px 18px rgba(15,23,42,0.12)' : '0 1px 3px rgba(15,23,42,0.07)' }}>
              {/* vehicle row — click to open/close */}
              <div
                onClick={() => { (open ? playClose : playOpen)(); setOpenId(open ? null : device.id); }}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
              >
                <span style={{
                  width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0,
                  background: worst === 'bad' ? '#ef4444' : worst === 'warn' ? '#f59e0b' : '#22c55e',
                  boxShadow: worst ? `0 0 8px ${worst === 'bad' ? 'rgba(239,68,68,0.6)' : 'rgba(245,158,11,0.6)'}` : 'none',
                }}></span>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', color: TXT, flex: 1 }}>
                  {dName(device)}
                </span>
                <span style={{
                  fontSize: '12px', fontWeight: 700, padding: '3px 12px', borderRadius: '999px',
                  background: worst === 'bad' ? '#fef2f2' : worst === 'warn' ? '#fffbeb' : '#f0fdf4',
                  color: worst === 'bad' ? '#b91c1c' : worst === 'warn' ? '#b45309' : '#15803d',
                }}>
                  {alerts.length === 0 ? 'ALL CLEAR' : `${alerts.length} ALERT${alerts.length > 1 ? 'S' : ''}`}
                </span>
                <span style={{ color: TXT_MUTED, fontSize: '13px', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
              </div>

              {/* details — appear on click, leave on second click */}
              {open && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f1f5f9' }}>
                  {position && (
                    <p style={{ fontSize: '12px', color: TXT_MUTED, margin: '10px 0' }}>
                      Last report: {new Date(position.fixTime).toLocaleString()}
                    </p>
                  )}
                  {alerts.length === 0 && (
                    <div style={{ background: '#f0fdf4', borderLeft: '4px solid #22c55e', borderRadius: '8px', padding: '12px 14px', color: '#15803d', fontSize: '14px', fontWeight: 600 }}>
                      ✅ No active alerts — engine, battery, temperature, and fuel all look good.
                    </div>
                  )}
                  {alerts.map((al, i) => {
                    const col = SEV[al.severity];
                    return (
                      <div key={i} style={{
                        background: col.bg, borderLeft: `4px solid ${col.border}`, borderRadius: '8px',
                        padding: '12px 14px', marginBottom: '8px',
                      }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: col.text, marginBottom: al.info || al.codes ? '4px' : 0 }}>
                          {al.icon} {al.title}
                        </div>
                        {al.info && <div style={{ fontSize: '13px', color: TXT_MUTED }}>{al.info}</div>}
                        {al.type === 'vin' && (
                          <button
                            onClick={() => { acceptVin(device.id, al.newVin); forceRefresh((n) => n + 1); }}
                            style={{
                              marginTop: '8px', padding: '7px 14px', borderRadius: '6px', fontSize: '12px',
                              fontWeight: 700, background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white',
                            }}
                          >
                            ✓ This change is OK — accept new VIN
                          </button>
                        )}
                        {al.codes && al.codes.map((c) => (
                          <div key={c.code} style={{ display: 'flex', gap: '10px', alignItems: 'baseline', marginTop: '6px' }}>
                            <span style={{
                              fontFamily: 'monospace', fontWeight: 700, fontSize: '13px', color: col.text,
                              background: 'rgba(255,255,255,0.7)', padding: '2px 8px', borderRadius: '5px',
                            }}>{c.code}</span>
                            <span style={{ fontSize: '13px', color: TXT_MUTED }}>{c.meaning}</span>
                          </div>
                        ))}
                        {al.ackKey && (
                          <button onClick={() => { ackHealth(device.id, al.ackKey, al.ackSig); forceRefresh((n) => n + 1); }} style={{ marginTop: '8px', background: 'white', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>✓ Mark resolved</button>
                        )}
                      </div>
                    );
                  })}

                  {/* ---- this car's alert profile: turn alerts on/off + custom rules ---- */}
                  <div style={{ marginTop: '14px', borderTop: '1px dashed #e2e8f0', paddingTop: '12px' }} data-v={prefsV}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: TXT, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>⚙️ Alerts for {dName(device)}</div>
                    {(() => { const prefs = getAlertPrefs(device.id); return BUILTIN_ALERTS.map((b) => {
                      const on = (prefs.builtins[b.key] || { on: true }).on !== false;
                      return (
                        <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: '1px solid #f8fafc' }}>
                          <span style={{ flex: 1, fontSize: '13px', color: '#334155' }}>{b.label}{(b.priority === 'critical' || b.priority === 'high') && <span style={{ fontSize: '9px', fontWeight: 800, color: '#b91c1c', background: '#fee2e2', borderRadius: '4px', padding: '1px 5px', marginLeft: '6px' }}>{b.priority.toUpperCase()}</span>}</span>
                          <button type="button" onClick={() => toggleBuiltin(device.id, b.key)} style={{ flexShrink: 0, width: '46px', height: '26px', minWidth: '46px', borderRadius: '999px', border: 'none', padding: 0, boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none', cursor: 'pointer', background: on ? 'linear-gradient(135deg,#22c55e,#16a34a)' : '#cbd5e1', position: 'relative', display: 'inline-block' }}>
                            <span style={{ position: 'absolute', top: '3px', left: on ? '23px' : '3px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 2px rgba(0,0,0,.3)', transition: 'left .15s' }} />
                          </button>
                        </div>
                      );
                    }); })()}
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px', marginBottom: '6px' }}>
                      <div style={{ flex: 1, fontSize: '11px', fontWeight: 800, color: TXT, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Custom rules</div>
                      <button onClick={() => { playOpen(); setRuleEdit({ ...emptyRule(), deviceId: String(device.id) }); }} style={{ fontSize: '11px', fontWeight: 700, color: 'white', background: 'linear-gradient(135deg,#f97316,#f4511e)', borderRadius: '6px', padding: '5px 10px' }}>+ Add</button>
                    </div>
                    {rules.filter((r) => String(r.deviceId) === String(device.id)).length === 0 && <div style={{ fontSize: '12px', color: TXT_MUTED }}>No custom rules for this car yet.</div>}
                    {rules.filter((r) => String(r.deviceId) === String(device.id)).map((r) => {
                      const m = METRICS.find((x) => x.key === r.metric); const o = OPS.find((x) => x.key === r.op);
                      return (
                        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: '1px solid #f8fafc', fontSize: '13px' }}>
                          <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: TXT }}>{r.name}</div><div style={{ fontSize: '12px', color: TXT_MUTED }}>{m && m.label} {o && o.label} {r.value}{m && m.unit}</div></div>
                          <button onClick={() => persistRules(rules.map((x) => (x.id === r.id ? { ...x, enabled: x.enabled === false } : x)))} style={{ fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px', background: r.enabled === false ? '#f1f5f9' : '#f0fdf4', color: r.enabled === false ? '#64748b' : '#15803d' }}>{r.enabled === false ? 'OFF' : 'ON'}</button>
                          <button onClick={() => { playOpen(); setRuleEdit({ ...r }); }} style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 700 }}>Edit</button>
                          <button onClick={() => { if (window.confirm(`Delete “${r.name}”?`)) persistRules(rules.filter((x) => x.id !== r.id)); }} style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 700 }}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ---- driver credential alerts ---- */}
        {driverRows.length > 0 && (
          <h2 style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: TXT, margin: '18px 0 2px' }}>
            👤 Drivers
          </h2>
        )}
        {driverRows.map(({ driver, alerts }) => {
          const key = `drv-${driver.id}`;
          const open = openId === key;
          const worst = alerts.some((al) => al.severity === 'bad') ? 'bad' : alerts.length ? 'warn' : null;
          return (
            <div key={key} style={{ ...ROW_CARD, boxShadow: open ? '0 6px 18px rgba(15,23,42,0.12)' : '0 1px 3px rgba(15,23,42,0.07)' }}>
              <div
                onClick={() => { (open ? playClose : playOpen)(); setOpenId(open ? null : key); }}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', cursor: 'pointer' }}
              >
                <span style={{
                  width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0,
                  background: worst === 'bad' ? '#ef4444' : worst === 'warn' ? '#f59e0b' : '#22c55e',
                  boxShadow: worst ? `0 0 8px ${worst === 'bad' ? 'rgba(239,68,68,0.6)' : 'rgba(245,158,11,0.6)'}` : 'none',
                }}></span>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', color: TXT, flex: 1 }}>
                  {driver.name} <span style={{ fontWeight: 400, fontSize: '12px', color: TXT_MUTED }}>· ID {driver.uniqueId}</span>
                </span>
                <span style={{
                  fontSize: '12px', fontWeight: 700, padding: '3px 12px', borderRadius: '999px',
                  background: worst === 'bad' ? '#fef2f2' : worst === 'warn' ? '#fffbeb' : '#f0fdf4',
                  color: worst === 'bad' ? '#b91c1c' : worst === 'warn' ? '#b45309' : '#15803d',
                }}>
                  {alerts.length === 0 ? 'ALL CLEAR' : `${alerts.length} ALERT${alerts.length > 1 ? 'S' : ''}`}
                </span>
                <span style={{ color: TXT_MUTED, fontSize: '13px', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
              </div>

              {open && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f1f5f9' }}>
                  {alerts.length === 0 && (
                    <div style={{ background: '#f0fdf4', borderLeft: '4px solid #22c55e', borderRadius: '8px', padding: '12px 14px', color: '#15803d', fontSize: '14px', fontWeight: 600, marginTop: '10px' }}>
                      ✅ Credentials in order — CDL and medical card are current.
                    </div>
                  )}
                  {alerts.map((al, i) => {
                    const col = SEV[al.severity];
                    return (
                      <div key={i} style={{
                        background: col.bg, borderLeft: `4px solid ${col.border}`, borderRadius: '8px',
                        padding: '12px 14px', marginTop: '10px',
                      }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: col.text, marginBottom: '4px' }}>
                          {al.icon} {al.title}
                        </div>
                        <div style={{ fontSize: '13px', color: TXT_MUTED }}>{al.info}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {detail && <AlertDetailModal alert={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
